import * as LDClient from "launchdarkly-js-client-sdk";
import {LDClientBase} from "launchdarkly-js-sdk-common";

interface LaunchDarklyType {
    tenantClients: { [key: string]: LDClientBase };
    pendingClients: { [key: string]: Promise<LDClientBase> };
    userClient: LDClientBase | null;
    companyClient: LDClientBase | null;
    anonymousClient: LDClientBase | null;

    createAnonymousClient(): Promise<LDClientBase>;

    createClient(context: any): Promise<LDClientBase>;

    setCompany(company: any): Promise<void>;
}

export const LaunchDarklyContextKind = {
    USER: 'user',
    TENANT: 'tenant',
    APPLICATION: 'application',
};


export const launchDarkly: LaunchDarklyType = {
    tenantClients: {},
    pendingClients: {},
    userClient: null,
    companyClient: null,
    anonymousClient: null,

    createAnonymousClient() {
        if (launchDarkly.anonymousClient != null) {
            return Promise.resolve(launchDarkly.anonymousClient);
        }
        return new Promise((resolve) => {
            const client = LDClient.initialize(window.ENVIRONMENT.LAUNCH_DARKLY_CLIENTSIDE_ID, {
                "kind": LaunchDarklyContextKind.USER,
                "key": "anonymous" + (window.ENVIRONMENT?.CONF_NAME != null ? `-${window.ENVIRONMENT.CONF_NAME}` : ''),
            });

            client.on('ready', () => {
                launchDarkly.anonymousClient = client;
                resolve(client);
            });
        });
    },

    createClient(context) {
        // keyed by kind too, so a user and a tenant that happen to share a key never share a client
        const cacheKey = `${context.kind}:${context.key}`;
        if (launchDarkly.tenantClients[cacheKey]) {
            return Promise.resolve(launchDarkly.tenantClients[cacheKey]);
        }
        // a second call for the same context while the first is still initializing reuses it, instead of opening another client
        if (launchDarkly.pendingClients[cacheKey]) {
            return launchDarkly.pendingClients[cacheKey];
        }

        const pending = new Promise<LDClientBase>((resolve) => {
            const client = LDClient.initialize(window.ENVIRONMENT.LAUNCH_DARKLY_CLIENTSIDE_ID, context);

            client.on('ready', () => {
                if (context.kind === LaunchDarklyContextKind.USER) {
                    launchDarkly.userClient = client;
                }

                if (context.kind === LaunchDarklyContextKind.TENANT) {
                    launchDarkly.companyClient = client;
                }

                launchDarkly.tenantClients[cacheKey] = client;
                delete launchDarkly.pendingClients[cacheKey];

                resolve(client);
            });
        });
        launchDarkly.pendingClients[cacheKey] = pending;
        return pending;
    },
    async setCompany(company) {
        if (company) {
            const tenantContext = {
                "kind": LaunchDarklyContextKind.TENANT,
                "key": company.tenantId || `company-${company.id}`,
                "name": company.name,
                "customer": company.idCustomer,
                "distributor": company.distributorName,
                "demo": company.demonstration,
                "odpcompanyId": company.odpcompanyId,
            };

            launchDarkly.companyClient = await launchDarkly.createClient(tenantContext);
        }
    }
};
